import { useState } from 'react';
import { AlertTriangle, ArrowRight, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useMe } from '@/api/domains/merchant-settings';
import type { ConfigIssueKey } from '@/api/types/settings';
import { cn } from '@/lib/utils';

/**
 * Where each issue's fix action points, and which section to flash on arrival.
 * Routes (and the highlight target) are a frontend concern, so the mapping lives
 * here rather than on the backend payload. The destination reads `?highlight=<section>`
 * via useHighlightSection to scroll + flash the matching block.
 */
const ISSUE_FIX: Record<ConfigIssueKey, { route: string; section: string }> = {
  inpost_not_connected: { route: '/authorization', section: 'inpost-connection' },
  no_active_delivery_method: { route: '/markets', section: 'delivery-methods' },
  sender_details_incomplete: { route: '/settings?tab=general', section: 'sender-details' },
  geowidget_token_missing: { route: '/authorization', section: 'geowidget-token' },
  test_mode_enabled: { route: '/settings?tab=checkout', section: 'test-mode' },
};

function fixLink(issue: ConfigIssueKey): string {
  const { route, section } = ISSUE_FIX[issue];
  const separator = route.includes('?') ? '&' : '?';
  return `${route}${separator}highlight=${section}`;
}

/**
 * Global configuration-health banner shown under the top bar on every authenticated
 * page; renders nothing when the configuration is healthy. A slim strip (icon + short
 * title + issue count + Review toggle) expands to the issue list, each row linking to
 * where the merchant can fix it.
 *
 * All health logic is backend-side: this reads the precomputed `config_health` from
 * `/admin/me` and only maps issue keys to copy + a fix destination. `problems` and
 * `warnings` are shown together in the strip's summary.
 */
export function ConfigHealthBanner() {
  const { t } = useTranslation('common');
  const { data } = useMe();
  const [open, setOpen] = useState(false);

  const issues: ConfigIssueKey[] = [
    ...(data?.config_health?.problems ?? []),
    ...(data?.config_health?.warnings ?? []),
  ];

  if (issues.length === 0) {
    return null;
  }

  return (
    <div className="border-b bg-background px-6 py-3 md:px-10">
      <div className="overflow-hidden rounded-xl border bg-background shadow-sm">
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="flex w-full items-center gap-2.5 px-4 py-3 text-left"
        >
          <AlertTriangle className="size-4 shrink-0 text-destructive" />
          <span className="text-sm font-medium text-foreground">
            {t('configHealth.shortTitle')}
          </span>
          <span className="flex size-5 items-center justify-center rounded-full bg-destructive/10 text-xs font-semibold text-destructive">
            {issues.length}
          </span>
          <span className="ml-auto inline-flex items-center gap-1 text-sm text-muted-foreground">
            {open ? t('configHealth.hide') : t('configHealth.review')}
            <ChevronDown className={cn('size-4 transition-transform', open && 'rotate-180')} />
          </span>
        </button>
        {open && (
          <ul className="border-t">
            {issues.map((issue) => (
              <li
                key={issue}
                className="flex items-center justify-between gap-3 border-t border-border/60 px-4 py-2.5 first:border-t-0"
              >
                <span className="text-sm text-muted-foreground">
                  {t(`configHealth.issues.${issue}`)}
                </span>
                <Link
                  to={fixLink(issue)}
                  className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-foreground hover:underline"
                >
                  {t('configHealth.fix')}
                  <ArrowRight className="size-3.5" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
